import axios from 'axios';
import { User } from 'firebase/auth';

export interface SegPoolAccount {
  userName: string;
  isPrimary: boolean;
}

const fetchVerifyAccounts = async (token: string): Promise<SegPoolAccount[]> => {
  const response = await axios.get('/pool/api/verify/accounts', {
    headers: {
      token,
      Authorization: `Bearer ${token}`,
    },
  });

  return Array.isArray(response.data?.accounts) ? response.data.accounts : [];
};

const normalizeUserName = (value: string): string => {
  const normalized = value.toLowerCase().replace(/[^a-z0-9]/g, '');
  return normalized.slice(0, 24);
};

const buildUserNameCandidates = (user: User): string[] => {
  const emailPrefix = user.email?.split('@')[0] ?? '';
  const displayName = user.displayName ?? '';
  const providerName = user.providerData[0]?.displayName ?? '';
  const uidSuffix = user.uid.replace(/[^a-z0-9]/gi, '').toLowerCase().slice(0, 6) || 'user';

  const seeds = [displayName, providerName, emailPrefix, 'segpooluser'];
  const candidates = seeds
    .map(normalizeUserName)
    .filter(Boolean)
    .flatMap((base) => [base, `${base}${uidSuffix}`]);

  return Array.from(new Set(candidates));
};

const buildGeneratedPassword = (): string => {
  const randomPart = Math.random().toString(36).slice(2, 12);
  return `Segpool!${randomPart}9`;
};

export const ensureSegPoolAccount = async (user: User, token: string): Promise<SegPoolAccount[]> => {
  const existingAccounts = await fetchVerifyAccounts(token);
  if (existingAccounts.length > 0) {
    return existingAccounts;
  }

  if (!user.email) {
    throw new Error('no_segpool_account');
  }

  const providerId = user.providerData[0]?.providerId ?? 'password';
  const candidates = buildUserNameCandidates(user);

  for (const userName of candidates) {
    try {
      await axios.post('/pool/auth/register', {
        userName,
        email: user.email,
        password: buildGeneratedPassword(),
        authProvider: providerId,
        firebaseUid: user.uid,
        displayName: user.displayName ?? '',
      }, {
        headers: {
          token,
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (_) {
      // Re-check after each attempt because the backend may have inserted the user
      // before returning an email/username conflict or other non-fatal response.
    }

    const accountsAfterAttempt = await fetchVerifyAccounts(token);
    if (accountsAfterAttempt.length > 0) {
      return accountsAfterAttempt;
    }
  }

  throw new Error('no_segpool_account');
};
